import { observer } from 'mobx-react'
import { useCallback, useContext, useEffect, useState, useRef, useMemo } from 'react'
import '@/components/SignDocumentCore/SignDocumentCore.css'
import '@/components/Generic/PdfViewer/core/Core.css'
import '@/components/Generic/PdfViewerOCG/PdfViewer.css'
import '@/components/Generic/PdfViewerOCG/addons/Addons.css'
import { rootStore, StoreContext } from '@store/Root.store'
import { onKeyDownHandler } from '@/methods/keyboardAccessibility'
import { useSignDocumentPolling } from '@/hooks/useSignDocumentPolling'
import { SignicatBranding } from '@components/SignicatBranding/SignicatBranding'
import { Buttons } from '@/components/SignDocumentCommon/Buttons/Buttons'
import { actionReport } from '@/methods/actionReport'
import { LoadingScreen } from '@/components/Generic/PdfViewer/core/LoadingScreen'
import { Button, Header, Icon, Popup } from '@/components/Generic'
import { Renderer } from '@/components/Generic/PdfViewerOCG/core/Renderer'
import { ScrollDocumentButton } from '@/components/Generic/PdfViewerOCG/addons/ScrollDocumentButton'
import { ErrorBoundary } from 'react-error-boundary'
import { devLog } from '@/methods/devLog'
import { PdfErrorContainer } from '@/components/Generic/PdfViewerOCG/core/PdfErrorContainer'
import { getDocumentUrl } from '@/methods/getDocumentUrl'
import { PdfViewerRef } from '@/components/Generic/PdfViewerOCG/types'

export const SignDocumentAmexPage = observer(() => {
  const rendererRef = useRef<PdfViewerRef>(null)

  const store = useContext(StoreContext)

  const { isMobile } = store.AppState
  const { context } = store.ScenarioState
  const { signDocumentPage: trans, pdfViewer: transViewer } =
    store.TranslationsState.translationsNew

  const checkboxClauseEnabled = !!context?.acceptUnfairClauses
  const showScrollToBottomButton = !!context?.showScrollToBottomButton
  const documentNeedsScrolling = context?.forceScrollingBeforeDocumentAccept

  const [infoPopupVisible, setInfoPopupVisible] = useState(
    !!context?.forceScrollingBeforeDocumentAccept
  )

  const [allowSigning, setAllowSigning] = useState<boolean>(true)

  const {
    isLoading,
    btnLoading,
    isDocReady,
    pdfFile,
    documentScrolled,
    setDocumentScrolled,
    checkboxKey,
    defaultCheckboxChecked,
    setDefaultCheckboxChecked,
    clauseCheckboxChecked,
    handleSign,
    isButtonDisabled,
  } = useSignDocumentPolling(false, checkboxClauseEnabled, documentNeedsScrolling, false)

  const handleCheckboxChange = useCallback(() => {
    if (!pdfFile?.id) return

    setDefaultCheckboxChecked(!defaultCheckboxChecked)

    if (!defaultCheckboxChecked) {
      actionReport({
        type: 'event.onboarding-web.agreement.AGREEMENT_ACKNOWLEDGED',
        payload: {
          name: pdfFile.name,
        },
      })
    } else {
      actionReport({
        type: 'event.onboarding-web.agreement.AGREEMENT_UNACKNOWLEDGED',
        payload: {
          name: pdfFile.name,
        },
      })
    }
  }, [setDefaultCheckboxChecked, defaultCheckboxChecked, pdfFile])

  useEffect(() => {
    actionReport({
      type: 'event.onboarding-web.eidv.SIGNING_PAGE_OPENED',
      payload: {},
    })
  }, [])

  const onDocumentLastPage = useCallback(() => {
    if (!documentScrolled && pdfFile?.id) {
      setDocumentScrolled(true)

      actionReport({
        type: 'event.onboarding-web.agreement.AGREEMENT_SCROLLED_TO_THE_END',
        payload: {
          documentId: pdfFile.id,
          name: pdfFile.name,
        },
      })
    }
  }, [])

  const showScrollToBottomButtonElement = useMemo(
    () => showScrollToBottomButton && !documentScrolled,
    [showScrollToBottomButton, documentScrolled]
  )

  const zoomButtonProps = useMemo(
    () => ({
      className: 'button--pdf-zoom' as const,
    }),
    []
  )
  return (
    <>
      <>
        <div className="sign-doc-core__container">
          <div className="sign-doc-page-header__container sign-doc-page-header__container--wide-controls">
            <Header className="no-margin text-left">{trans.header}</Header>

            <div className="pdf-ocg-addons__external-zoom-control-wrap">
              <Button onClick={() => rendererRef.current?.fitToWidth()} {...zoomButtonProps}>
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" width={32} height={32}>
                  <path
                    d="M4 4h16v2H4zm0 14h16v2H4zm2-7h2v2H6zm4 0h4v2h-4zm6 0h2v2h-2z"
                    fill="currentColor"
                  />
                  <path d="M2 8h2v8H2zm18 0h2v8h-2z" fill="currentColor" />
                </svg>
              </Button>

              <Button
                onClick={() => rendererRef.current?.zoomOut()}
                {...zoomButtonProps}
                aria-label={rootStore.TranslationsState.translationsNew.zoomButtons.zoomOut}
              >
                <Icon type="zoom-out" size="30px" />
              </Button>

              <Button
                onClick={() => rendererRef.current?.zoomIn()}
                {...zoomButtonProps}
                aria-label={rootStore.TranslationsState.translationsNew.zoomButtons.zoomIn}
              >
                <Icon type="zoom-in" size="30px" />
              </Button>
            </div>
          </div>

          {isLoading || !isDocReady ? (
            <LoadingScreen>
              <div className="pdf-core__loading-text">{transViewer?.loading}</div>
            </LoadingScreen>
          ) : (
            <ErrorBoundary
              fallbackRender={() => (
                <PdfErrorContainer
                  failedToLoadPdf={transViewer?.cantDisplayTheDocument || ''}
                  setAllowSigning={setAllowSigning}
                  documentUrl={pdfFile?.url || getDocumentUrl(pdfFile?.id, 'electronic-id')}
                />
              )}
              onError={(error, errorInfo) => {
                devLog('Error caught by boundary in pdf viewer:', error, errorInfo)

                actionReport({
                  type: 'event.onboarding-web.process.REACT_ERROR_BOUNDARY_CATCH',
                  payload: {
                    error: {
                      message: error.message,
                      stack: error.stack,
                      name: error.name,
                    },
                    componentStack: errorInfo.componentStack,
                    userAgent: navigator.userAgent,
                  },
                })
              }}
            >
              <div className="pdf-ocg__container pdf-viewer-container">
                <Renderer
                  ref={rendererRef}
                  source={{
                    type: 'url',
                    url: pdfFile?.url || getDocumentUrl(pdfFile?.id, 'electronic-id'),
                  }}
                  onDocumentLastPage={onDocumentLastPage}
                  onError={() => {
                    setAllowSigning(false)
                  }}
                  onDocumentLoadSuccess={() => setAllowSigning(true)}
                  setAllowSigning={setAllowSigning}
                />
                {/* 
                      <PageIndicator
                        currentPage={currentPageIndex}
                        totalPages={totalPages}
                        shouldShow={shouldShowPageIndicator}
                        setShouldShow={setShouldShowPageIndicator}
                      /> */}

                <ScrollDocumentButton
                  onClick={onDocumentLastPage}
                  bottomOffset={0}
                  showScrollToBottomButton={showScrollToBottomButtonElement}
                />
              </div>
            </ErrorBoundary>
          )}

          <div className="sign-doc-core__footer">
            <div className="doc-core__checkbox-form">
              <label
                className="label-checkbox"
                onKeyDown={(event) => onKeyDownHandler(event, handleCheckboxChange)}
                tabIndex={0}
              >
                <input
                  key={'checkbox-' + checkboxKey}
                  type="checkbox"
                  name="sign"
                  value={defaultCheckboxChecked.toString()}
                  checked={defaultCheckboxChecked}
                  id="sign"
                  onChange={handleCheckboxChange}
                  tabIndex={-1}
                />
                <span>{trans.checkboxLabel}</span>
              </label>
            </div>

            <div className="sign-doc-core__footer-bottom">
              {!isMobile && (
                <SignicatBranding width="fit-content" margin="0" marginMobile="0" onSignDocument />
              )}

              <Buttons
                documentId={pdfFile?.id ?? ''}
                defaultCheckboxChecked={defaultCheckboxChecked}
                checkboxClauseEnabled={checkboxClauseEnabled}
                clauseCheckboxChecked={clauseCheckboxChecked}
                showButtonHint={false}
                handleSign={handleSign}
                setAllowSigning={setAllowSigning}
                isButtonDisabled={isButtonDisabled || !allowSigning}
                btnLoading={btnLoading}
                transProceed={trans.proceed}
                transReject={{
                  rejectButtonText: trans.rejectButtonText,
                  rejectPopupTitle: trans.rejectPopupTitle,
                  backButtonText: trans.backButtonText,
                  confirmButtonText: trans.confirmButtonText,
                }}
                transHint={trans.hint}
              />
            </div>
          </div>
        </div>
      </>

      <Popup
        visibility={infoPopupVisible}
        handleVisibility={setInfoPopupVisible}
        className="generic__scroll-document-popup sign-doc-core__popup"
      >
        <p className="generic__scroll-document-popup-body">{trans.popupDescription}</p>
        <div className="generic__spacer h-20" />
        <div className="layout__content-to-right">
          <Button onClick={() => setInfoPopupVisible(false)} className="sign-doc-core__popup-btn">
            {trans.popupButton}
          </Button>
        </div>
      </Popup>
    </>
  )
})
